import React, { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop } from 'lodash';
import Tooltip from 'rc-tooltip';

import Checkbox from '../../../webcommon/common/checkbox/Checkbox';
import Template from '../../../webcommon/common/Icons/Templates.svg';

import defaultStyles from './SummaryInfo.module.scss';
import SummaryGrid from './SummaryGrid';
import MonitoringStatusPill from '../../../common/monitoringStatusPill/MonitoringStatusPill';

const valueIsNotNull = value => (
    value || value === 0
);

const SummaryInfo = (props) => {
    const {
        setShowDefaultMappings,
        status,
        styles,
        transactionId
    } = props;

    const showDefaultMappingsFunc = useCallback((checked) => {
        setShowDefaultMappings(checked);
    }, [setShowDefaultMappings]);

    const isMonitoring = useMemo(() => status !== null, [status]);

    const textWrapper = useMemo(() => classnames({
        [styles.textWrapper]: true,
        [styles.monitoringWrapper]: isMonitoring
    }), [styles.textWrapper, styles.monitoringWrapper, isMonitoring]);

    const light = useMemo(() => classnames({
        [styles.light]: true,
        [styles.monitoringlight]: isMonitoring
    }), [styles.light, styles.monitoringlight, isMonitoring]);

    const dark = useMemo(() => classnames({
        [styles.dark]: true,
        [styles.monitoringdark]: isMonitoring
    }), [styles.dark, styles.monitoringdark, isMonitoring]);

    const transactionIdWrapper = useMemo(() => classnames({
        [styles.transactionIdWrapper]: true,
        [styles.hidden]: transactionId === null
    }), [styles.transactionIdWrapper, styles.hidden, transactionId]);

    const textAreaRef = useRef(null);

    const copyId = () => {
        textAreaRef.current.select();
        document.execCommand('copy');
        props.copyTransactionId('ID Copied');
    };

    return (
        <div className={ props.styles.summaryInfo }>
            <hr className={ props.styles.horizontalRow } />
            <div className={ props.styles.titleWrapper }>
                <div className={ props.styles.leftWrapper }>
                    <div className={ props.styles.title }>
                        { props.currentType } CSA Summary
                    </div>
                    <div className={ props.styles.showDefaultMappings }>
                        Show Default Product Class Mappings
                    </div>
                    <div className={ props.styles.gridCheckbox }>
                        <Checkbox
                            checked={ props.showDefaultMappings }
                            onChange={ showDefaultMappingsFunc }
                        />
                    </div>
                    { isMonitoring ? (
                        <div className={ props.styles.statusWrapper }>
                            <MonitoringStatusPill
                                status={ props.status.status }
                                colour={ props.status.colour }
                            />
                        </div>
                    ) : null }
                </div>
                <div className={ transactionIdWrapper }>
                    <div className={ props.styles.filterHeaderText }>
                        Impact Analysis ID:
                    </div>
                    <textarea
                        ref={ textAreaRef }
                        readOnly
                        className={ props.styles.transactionIdText }
                        value={ props.transactionId || '' }
                    />
                    <Tooltip
                        placement="top"
                        overlay="Copy Impact Analysis ID"
                        arrowContent={ <div className="rc-tooltip-arrow-inner" /> }
                    >
                        <div className={ props.styles.copyButton } href="#">
                            <button onClick={ () => copyId() } className={ props.styles.copyButtonWrapper } type="button">
                                <img
                                    alt="Copy"
                                    src={ Template }
                                    className={ props.styles.svg }
                                />
                            </button>
                        </div>
                    </Tooltip>
                </div>
            </div>

            <div className={ props.styles.textContainer }>
                <div className={ textWrapper }>
                    <div className={ light }>Cross Currency (excl. Principal)</div>
                    <div className={ dark }>{props.excludePrincipal}</div>
                </div>
                <div className={ textWrapper }>
                    <div className={ light }>{props.showEuros ? 'EOD IM Pledge EUR' : 'EOD IM Pledge USD'}</div>
                    <div className={ dark }>
                        {props.showEuros ? `€${valueIsNotNull(props.imPledgeEur) ? props.imPledgeEur.toLocaleString() : ''}`
                            : `$${valueIsNotNull(props.imPledgeUsd) ? props.imPledgeUsd.toLocaleString() : ''}`}
                    </div>
                </div>
                { isMonitoring ? (
                    <div className={ textWrapper }>
                        <div className={ light }>{ props.showEuros ? 'Threshold Level 1 EUR' : 'Threshold Level 1 USD' }</div>
                        <div className={ dark }>
                            {props.showEuros ? `€${valueIsNotNull(props.monitoringThresholdLevel1Eur) ? props.monitoringThresholdLevel1Eur.toLocaleString() : ''}`
                                : `$${valueIsNotNull(props.monitoringThresholdLevel1Usd) ? props.monitoringThresholdLevel1Usd.toLocaleString() : ''}`}
                        </div>
                    </div>
                ) : null}
                <div className={ textWrapper }>
                    <div className={ light }>Calculation Currency Collect</div>
                    <div className={ dark }>{props.calcCurrencyCollect}</div>
                </div>
                <div className={ textWrapper }>
                    <div className={ light }>{props.showEuros ? 'IM Threshold Collect EUR' : 'IM Threshold Collect USD'}</div>
                    <div className={ dark }>
                        {props.showEuros ? `€${valueIsNotNull(props.imThresholdCollectEur) ? props.imThresholdCollectEur.toLocaleString() : ''}`
                            : `$${valueIsNotNull(props.imThresholdCollectUsd) ? props.imThresholdCollectUsd.toLocaleString() : ''}`}
                    </div>
                </div>
            </div>

            <div className={ props.styles.textContainer }>
                <div className={ textWrapper }>
                    <div className={ light }>Options (excl. Short positions)</div>
                    <div className={ dark }>{props.excludeShortOption}</div>
                </div>
                <div className={ textWrapper }>
                    <div className={ light }>{props.showEuros ? 'EOD IM Receive EUR' : 'EOD IM Receive USD'}</div>
                    <div className={ dark }>
                        {props.showEuros ? `€${valueIsNotNull(props.imReceiveEur) ? props.imReceiveEur.toLocaleString() : ''}`
                            : `$${valueIsNotNull(props.imReceiveUsd) ? props.imReceiveUsd.toLocaleString() : ''}`}
                    </div>
                </div>
                { isMonitoring ? (
                    <div className={ textWrapper }>
                        <div className={ light }>{ props.showEuros ? 'Threshold Level 2 EUR' : 'Threshold Level 2 USD' }</div>
                        <div className={ dark }>
                            {props.showEuros ? `€${valueIsNotNull(props.monitoringThresholdLevel2Eur) ? props.monitoringThresholdLevel2Eur.toLocaleString() : ''}`
                                : `$${valueIsNotNull(props.monitoringThresholdLevel2Usd) ? props.monitoringThresholdLevel2Usd.toLocaleString() : ''}`}
                        </div>
                    </div>
                ) : null }
                <div className={ textWrapper }>
                    <div className={ light }>Calculation Currency Post</div>
                    <div className={ dark }>{props.calcCurrencyPost}</div>
                </div>
                <div className={ textWrapper }>
                    <div className={ light }>{props.showEuros ? 'IM Threshold Post EUR' : 'IM Threshold Post USD'}</div>
                    <div className={ dark }>
                        {props.showEuros ? `€${valueIsNotNull(props.imThresholdPostEur) ? props.imThresholdPostEur.toLocaleString() : ''}`
                            : `$${valueIsNotNull(props.imThresholdPostUsd) ? props.imThresholdPostUsd.toLocaleString() : ''}`}
                    </div>
                </div>
                <div />
            </div>
            {props.showDefaultMappings && (
                <SummaryGrid
                    loading={ props.loading }
                    summaryGridColumns={ props.summaryGridColumns }
                    summaryGridRows={ props.summaryGridRows }
                />
            ) }
        </div>
    );
};

SummaryInfo.propTypes = {
    calcCurrencyCollect: PropTypes.string,
    calcCurrencyPost: PropTypes.string,
    excludePrincipal: PropTypes.string,
    loading: PropTypes.bool,
    imPledgeEur: PropTypes.number,
    imReceiveEur: PropTypes.number,
    imThresholdCollectEur: PropTypes.number,
    imThresholdPostEur: PropTypes.number,
    excludeShortOption: PropTypes.string,
    imReceiveUsd: PropTypes.number,
    imThresholdCollectUsd: PropTypes.number,
    imThresholdPostUsd: PropTypes.number,
    imPledgeUsd: PropTypes.number,
    setShowDefaultMappings: PropTypes.func,
    showDefaultMappings: PropTypes.bool,
    showEuros: PropTypes.bool,
    styles: PropTypes.objectOf(PropTypes.string),
    summaryGridColumns: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            id: PropTypes.string
        })
    ),
    summaryGridRows: PropTypes.arrayOf(PropTypes.shape({})),
    monitoringThresholdLevel1Eur: PropTypes.number,
    monitoringThresholdLevel2Eur: PropTypes.number,
    monitoringThresholdLevel1Usd: PropTypes.number,
    monitoringThresholdLevel2Usd: PropTypes.number,
    status: PropTypes.objectOf(PropTypes.string),
    currentType: PropTypes.string,
    transactionId: PropTypes.string,
    copyTransactionId: PropTypes.func
};

SummaryInfo.defaultProps = {
    calcCurrencyCollect: '',
    calcCurrencyPost: '',
    excludePrincipal: '',
    loading: false,
    imPledgeEur: 0,
    imReceiveEur: 0,
    excludeShortOption: '',
    imReceiveUsd: 0,
    imPledgeUsd: 0,
    setShowDefaultMappings: noop,
    copyTransactionId: noop,
    showDefaultMappings: false,
    showEuros: false,
    styles: defaultStyles,
    summaryGridColumns: [],
    summaryGridRows: [],
    monitoringThresholdLevel1Eur: 0,
    monitoringThresholdLevel2Eur: 0,
    monitoringThresholdLevel1Usd: 0,
    monitoringThresholdLevel2Usd: 0,
    status: null,
    currentType: '',
    transactionId: '',
    imThresholdCollectEur: 0,
    imThresholdCollectUsd: 0,
    imThresholdPostEur: 0,
    imThresholdPostUsd: 0
};

export default SummaryInfo;
